import Box from '@amzn/meridian/box';
import Link from '@amzn/meridian/link';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { TableCellWidth } from '@amzn/meridian/table/table-cell';
import { css } from 'emotion';
import _ from 'lodash';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { Debug } from '../../../utils/debug';
import { Button, TranslatedString, useTranslation } from '../../blocks/localization';
import { Tile } from '../../composites';
import { Delete } from '../../icons';

export type MultilineFormEditorColumnFormat<T = any> = {
    render: (
        item: T,
        column: MultilineFormEditorColumn,
        itemId: string,
        onChange: (fieldName: string, itemId: string) => (newValue: T) => void
    ) => ReactElement | null;
};

export type MultilineFormEditorColumn = {
    title: TranslatedString;
    columnHeader?: React.ReactNode;
    isKey?: boolean;
    width?: TableCellWidth;
    sourceProperty: string;
    sortable?: boolean;
    sortFunction?: (a: any, b: any) => number;
} & (
    | { isVisible?: boolean; format: MultilineFormEditorColumnFormat }
    | { isVisible: false; format?: MultilineFormEditorColumnFormat }
);

export interface MultilineFormEditorProps<T = any> {
    title: TranslatedString;
    maxHeight?: number;
    columns: MultilineFormEditorColumn[];
    items: T[];
    onItemChange: (items: T[], changedIndex?: number, changedField?: string) => void;
    onAddRowClick?: () => void;
    addRowButtonLabel?: TranslatedString;
    onDeleteRowClick?: (items: T[], index: number) => void;
    deleteRowButtonLabel?: TranslatedString;
    newItemBuilder: () => T;
    dataTestId?: string;
    sortable?: boolean;
    defaultSortColumn?: string;
    defaultSortDirection?: 'ascending' | 'descending';
    idField?: string;
    tileHeader?: React.ReactNode;
}

const scrollClass = css`
    overflow-y: auto;
`;

type SortConfig = {
    column: string;
    direction: 'ascending' | 'descending';
} | null;

const MultilineFormEditor: FC<MultilineFormEditorProps> = ({
    title,
    maxHeight,
    columns,
    items,
    onItemChange,
    onAddRowClick,
    addRowButtonLabel,
    onDeleteRowClick,
    deleteRowButtonLabel,
    newItemBuilder,
    dataTestId,
    sortable = false,
    defaultSortColumn,
    defaultSortDirection = 'ascending',
    idField = 'id',
    tileHeader,
}: MultilineFormEditorProps) => {
    const keyColumn = _.find(columns, 'isKey');
    Debug.assertExists(keyColumn);
    const keyProperty = keyColumn.sourceProperty;

    const addRow = () => {
        if (onAddRowClick) {
            onAddRowClick();
        } else {
            onItemChange([...items, newItemBuilder()]);
        }
    };

    const removeRow = (idToRemove: string) => {
        const indexToRemove = items.findIndex((item) => item[idField] === idToRemove);
        if (onDeleteRowClick) {
            onDeleteRowClick(items, indexToRemove);
        } else {
            onItemChange(items.filter((item) => item[idField] !== idToRemove));
        }
    };

    function handleItemFieldChange(fieldName: string, changedId: string) {
        const changedIndex = items.findIndex((item) => item[idField] === changedId);
        return (value: any): void => {
            onItemChange(
                items.map((item) => (item[idField] === changedId ? { ...item, [fieldName]: value } : item)),
                changedIndex,
                fieldName
            );
        };
    }

    const [sortConfig, setSortConfig] = useState<SortConfig>(() =>
        sortable && defaultSortColumn ? { column: defaultSortColumn, direction: defaultSortDirection } : null
    );

    const handleSort = (columnName: string) => {
        if (!sortable) return;

        setSortConfig((currentConfig) => {
            if (!currentConfig || currentConfig.column !== columnName) {
                return { column: columnName, direction: 'ascending' };
            }
            if (currentConfig.direction === 'ascending') {
                return { column: columnName, direction: 'descending' };
            }
            return null;
        });
    };

    const sortedItems = useMemo(() => {
        if (!sortable || !sortConfig) return items;
        return [...items].sort((a, b) => {
            const column = columns.find((col) => col.sourceProperty === sortConfig.column);

            // Custom sorting
            if (column?.sortFunction) {
                const result = column.sortFunction(a, b);
                return sortConfig.direction === 'ascending' ? result : -result;
            }

            // Default sorting
            const aValue = a[sortConfig.column];
            const bValue = b[sortConfig.column];

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
    }, [items, sortConfig, sortable, columns]);

    useEffect(() => {
        if (sortable && defaultSortColumn) {
            setSortConfig({ column: defaultSortColumn, direction: defaultSortDirection });
        }
    }, [sortable, defaultSortColumn, defaultSortDirection]);

    const { t } = useTranslation('createReturnOrderPage');
    return (
        <Tile title={title} width={'100%'} dataTestId={dataTestId}>
            {addRowButtonLabel && (
                <Button type={'link'} onClick={addRow} data-testid={dataTestId && `${dataTestId}.addRowButton`}>
                    {addRowButtonLabel}
                </Button>
            )}
            {tileHeader}
            <Box className={scrollClass} spacingInset={'small'} width={'100%'} maxHeight={maxHeight}>
                <Table
                    className={scrollClass}
                    headerRows={1}
                    spacing={'small'}
                    fixHeaderRows={true}
                    sortColumn={sortConfig?.column}
                    sortDirection={sortConfig?.direction}
                    onSort={sortable ? ({ sortColumn }) => handleSort(sortColumn) : undefined}
                >
                    <TableRow data-testid={dataTestId && `${dataTestId}.tableHeaderRow`}>
                        {columns
                            .filter((column) => column.isVisible ?? true)
                            .map((column) => (
                                <TableCell
                                    sortColumn={column.sortable ? column.sourceProperty : undefined}
                                    key={column.title}
                                    width={column.width}
                                    data-testid={dataTestId && `${dataTestId}.column[${column.title}].headerCell`}
                                >
                                    {column.columnHeader}
                                    {!column.columnHeader && column.title}
                                </TableCell>
                            ))}
                    </TableRow>
                    {sortedItems.map((item) => (
                        <TableRow
                            key={item[keyProperty]}
                            data-testid={dataTestId && `${dataTestId}.row[${item[keyProperty]}]`}
                        >
                            {columns
                                .filter((column) => column.isVisible ?? true)
                                .map((column) => (
                                    <TableCell
                                        key={item[keyProperty] + column.title}
                                        data-testid={
                                            dataTestId &&
                                            `${dataTestId}.row[${item[keyProperty]}].column[${column.title}].valueCell`
                                        }
                                    >
                                        {column.format!.render(item, column, item[idField], handleItemFieldChange)}
                                    </TableCell>
                                ))}
                            <TableCell>
                                {deleteRowButtonLabel ? (
                                    <Button
                                        type={'link'}
                                        onClick={() => removeRow(item[idField])}
                                        data-testid={
                                            dataTestId && `${dataTestId}.row[${item[keyProperty]}].deleteRowButton`
                                        }
                                    >
                                        {deleteRowButtonLabel}
                                    </Button>
                                ) : (
                                    <Link
                                        onClick={() => removeRow(item[idField])}
                                        type={'secondary'}
                                        data-testid={
                                            dataTestId && `${dataTestId}.row[${item[keyProperty]}].deleteRowButton`
                                        }
                                        aria-label={t('deleteOrderLine-buttonLabel')}
                                    >
                                        <Delete />
                                    </Link>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </Table>
            </Box>
        </Tile>
    );
};

export { MultilineFormEditor };
